import React, { useState, useEffect } from 'react';
import { X, Download, Save, Calculator, MapPin } from 'lucide-react';
import { supabase } from '../lib/supabase';
import '../styles/CostBreakdown.css';

interface ComponentKey {
  color: string;
  name: string;
  symbol: string;
  pages: Array<{ page: number; count: number }>;
  totalCount: number;
}

interface ComponentCost {
  name: string;
  count: number;
  materialCost: number;
  laborUnits: number; // NECA labor units
  materialTotal: number;
  laborTotal: number;
  total: number;
}

interface CostBreakdownProps {
  estimationId: string;
  projectKey: any;
  annotationCounts: Map<string, Map<number, number>>;
  onClose: () => void;
}

export const CostBreakdown: React.FC<CostBreakdownProps> = ({
  estimationId,
  projectKey,
  annotationCounts,
  onClose
}) => {
  const [laborRate, setLaborRate] = useState<number>(85); // Default labor rate per hour
  const [componentCosts, setComponentCosts] = useState<ComponentCost[]>([]);
  const [overhead, setOverhead] = useState<number>(20); // Overhead percentage (NECA standard)
  const [profit, setProfit] = useState<number>(15); // Profit margin percentage
  const [location, setLocation] = useState<string>('Detecting location...');
  const [costMultiplier, setCostMultiplier] = useState<number>(1.0);
  const [isLoadingLocation, setIsLoadingLocation] = useState<boolean>(true);

  // NECA labor unit standards (hours per unit) - Based on NECA Manual of Labor Units
  const necaLaborUnits: { [key: string]: number } = {
    'outlet': 0.6,        // Standard duplex receptacle
    'switch': 0.5,        // Single pole switch
    'light': 1.5,         // Light fixture replacement (1-2 hrs per NECA)
    'fixture': 1.5,       // Light fixture installation
    'panel': 8.0,         // Panel replacement (full day)
    'breaker': 0.4,       // Single breaker
    'receptacle': 0.6,    // Standard receptacle
    'gfci': 0.75,         // GFCI outlet (more complex)
    'dimmer': 0.6,        // Dimmer switch
    'fan': 2.5,           // Ceiling fan with light
    'smoke detector': 0.8,
    'exhaust fan': 1.2,
    'default': 0.75
  };

  // Base material costs (will be adjusted by location multiplier) - Realistic market pricing
  const baseMaterialCosts: { [key: string]: number } = {
    'outlet': 4.50,          // Standard duplex receptacle
    'switch': 3.50,          // Single pole switch
    'light': 125.00,         // Light fixture ($50-200 range, using midpoint)
    'fixture': 125.00,       // Light fixture
    'panel': 450.00,         // Electrical panel
    'breaker': 35.00,        // Circuit breaker
    'receptacle': 4.50,      // Standard receptacle
    'gfci': 18.00,           // GFCI outlet
    'dimmer': 25.00,         // Dimmer switch
    'fan': 150.00,           // Ceiling fan with light
    'smoke detector': 35.00,
    'exhaust fan': 85.00,
    'default': 15.00
  };

  // Regional cost data (labor rates and material cost multipliers) - Based on NECA regional data
  const regionalData: { [key: string]: { laborRate: number; materialMultiplier: number } } = {
    // High cost areas (Based on NECA contractor rates)
    'California': { laborRate: 140, materialMultiplier: 1.35 },      // SF Bay Area pricing
    'New York': { laborRate: 125, materialMultiplier: 1.30 },        // NYC metro pricing
    'Massachusetts': { laborRate: 115, materialMultiplier: 1.25 },   // Boston area
    'Washington': { laborRate: 110, materialMultiplier: 1.22 },      // Seattle area
    'Hawaii': { laborRate: 135, materialMultiplier: 1.50 },          // Island premium
    'Alaska': { laborRate: 130, materialMultiplier: 1.45 },          // Remote premium
    'Connecticut': { laborRate: 112, materialMultiplier: 1.24 },
    'New Jersey': { laborRate: 118, materialMultiplier: 1.26 },      // NYC metro area
    
    // Medium-high cost areas
    'Colorado': { laborRate: 95, materialMultiplier: 1.15 },         // Denver area
    'Oregon': { laborRate: 98, materialMultiplier: 1.18 },           // Portland area
    'Illinois': { laborRate: 100, materialMultiplier: 1.16 },        // Chicago area
    'Maryland': { laborRate: 105, materialMultiplier: 1.20 },        // DC metro
    'Virginia': { laborRate: 92, materialMultiplier: 1.14 },         // DC metro
    
    // Medium cost areas
    'Florida': { laborRate: 78, materialMultiplier: 1.08 },
    'Texas': { laborRate: 82, materialMultiplier: 1.10 },            // Major cities
    'Arizona': { laborRate: 80, materialMultiplier: 1.09 },          // Phoenix area
    'Nevada': { laborRate: 88, materialMultiplier: 1.12 },           // Las Vegas area
    'North Carolina': { laborRate: 75, materialMultiplier: 1.05 },
    'Georgia': { laborRate: 77, materialMultiplier: 1.06 },          // Atlanta area
    
    // Lower cost areas
    'Ohio': { laborRate: 72, materialMultiplier: 1.02 },
    'Pennsylvania': { laborRate: 74, materialMultiplier: 1.04 },
    'Michigan': { laborRate: 73, materialMultiplier: 1.03 },
    'Tennessee': { laborRate: 70, materialMultiplier: 1.00 },
    'Indiana': { laborRate: 68, materialMultiplier: 0.98 },
    'Alabama': { laborRate: 67, materialMultiplier: 0.97 },
    'Mississippi': { laborRate: 65, materialMultiplier: 0.95 },
    'Arkansas': { laborRate: 66, materialMultiplier: 0.96 },
    'Oklahoma': { laborRate: 69, materialMultiplier: 0.98 },
    'Kentucky': { laborRate: 68, materialMultiplier: 0.97 },
    'Louisiana': { laborRate: 71, materialMultiplier: 1.00 },
    'Missouri': { laborRate: 72, materialMultiplier: 1.01 },
    'Wisconsin': { laborRate: 76, materialMultiplier: 1.04 },
    'Minnesota': { laborRate: 85, materialMultiplier: 1.10 },
    
    // Default for unlisted states
    'default': { laborRate: 85, materialMultiplier: 1.0 }
  };

  // Get user location on mount
  useEffect(() => {
    getUserLocation();
  }, []);

  useEffect(() => {
    if (projectKey && annotationCounts) {
      calculateCosts();
    }
  }, [projectKey, annotationCounts, laborRate, costMultiplier]);

  // Recalculate when overhead or profit changes
  useEffect(() => {
    // This triggers a re-render with updated calculations
  }, [overhead, profit]);

  // Handle ESC key to close
  useEffect(() => {
    const handleEscKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscKey);
    return () => document.removeEventListener('keydown', handleEscKey);
  }, [onClose]);

  const getUserLocation = async () => {
    setIsLoadingLocation(true);
    
    if (!navigator.geolocation) {
      setLocation('Location not available');
      setIsLoadingLocation(false);
      return;
    }

    try {
      navigator.geolocation.getCurrentPosition(
        async (position) => {
          const { latitude, longitude } = position.coords;
          
          // Reverse geocode to get location details
          try {
            const response = await fetch(
              `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}`
            );
            const data = await response.json();
            
            const state = data.address?.state || '';
            const city = data.address?.city || data.address?.town || data.address?.village || '';
            const country = data.address?.country || '';
            
            console.log('📍 Detected location:', { city, state, country });
            
            // Find regional data for this state
            let regionalInfo = regionalData['default'];
            if (state && regionalData[state]) {
              regionalInfo = regionalData[state];
            }
            
            // Apply regional adjustments
            setLaborRate(regionalInfo.laborRate);
            setCostMultiplier(regionalInfo.materialMultiplier);
            
            const locationString = city && state 
              ? `${city}, ${state}` 
              : state || country || 'Unknown Location';
            
            setLocation(locationString);
            setIsLoadingLocation(false);
            
            console.log(`💰 Adjusted rates for ${locationString}: Labor $${regionalInfo.laborRate}/hr, Material multiplier ${regionalInfo.materialMultiplier}x`);
          } catch (error) {
            console.error('Geocoding error:', error);
            setLocation('Location detected');
            setIsLoadingLocation(false);
          }
        },
        (error) => {
          console.error('Geolocation error:', error);
          setLocation('Using default rates');
          setIsLoadingLocation(false);
        },
        {
          enableHighAccuracy: false,
          timeout: 10000,
          maximumAge: 300000 // Cache for 5 minutes
        }
      );
    } catch (error) {
      console.error('Error getting location:', error);
      setLocation('Using default rates');
      setIsLoadingLocation(false);
    }
  };

  const calculateCosts = () => {
    const costs: ComponentCost[] = [];

    projectKey.components.forEach((component: ComponentKey) => {
      const componentCounts = annotationCounts.get(component.name);
      let totalCount = 0;

      if (componentCounts) {
        componentCounts.forEach((count) => {
          totalCount += count;
        });
      }

      // Include ALL components, even with 0 count

      // Get NECA labor units for this component type
      const laborUnitsPerItem = necaLaborUnits[component.name.toLowerCase()] || necaLaborUnits['default'];
      
      // Get material cost for this component type (adjusted by regional multiplier)
      const baseCost = baseMaterialCosts[component.name.toLowerCase()] || baseMaterialCosts['default'];
      const materialCostPerItem = baseCost * costMultiplier;

      const materialTotal = totalCount * materialCostPerItem;
      const totalLaborHours = totalCount * laborUnitsPerItem;
      const laborTotal = totalLaborHours * laborRate;
      const total = materialTotal + laborTotal;

      costs.push({
        name: component.name,
        count: totalCount,
        materialCost: materialCostPerItem,
        laborUnits: laborUnitsPerItem,
        materialTotal,
        laborTotal,
        total
      });
    });

    setComponentCosts(costs);
  };

  const handleCostChange = (index: number, field: 'materialCost' | 'laborUnits', value: number) => {
    const updated = [...componentCosts];
    updated[index][field] = value;
    
    // Recalculate totals
    const materialTotal = updated[index].count * updated[index].materialCost;
    const laborTotal = updated[index].count * updated[index].laborUnits * laborRate;
    updated[index].materialTotal = materialTotal;
    updated[index].laborTotal = laborTotal;
    updated[index].total = materialTotal + laborTotal;
    
    setComponentCosts(updated);
  };

  // Calculate totals
  const subtotalMaterial = componentCosts.reduce((sum, item) => sum + item.materialTotal, 0);
  const subtotalLabor = componentCosts.reduce((sum, item) => sum + item.laborTotal, 0);
  const subtotal = subtotalMaterial + subtotalLabor;
  const overheadAmount = (subtotal * overhead) / 100;
  const profitAmount = (subtotal * profit) / 100;
  const grandTotal = subtotal + overheadAmount + profitAmount;
  const totalLaborHours = componentCosts.reduce((sum, item) => sum + (item.count * item.laborUnits), 0);

  const exportToCSV = () => {
    let csv = `Cost Breakdown - ${location}\n`;
    csv += `Generated: ${new Date().toLocaleString()}\n`;
    csv += `Labor Rate: $${laborRate.toFixed(2)}/hr | Material Multiplier: ${costMultiplier.toFixed(2)}x\n\n`;
    
    csv += 'Component,Quantity,Material Cost (ea),Labor Units (hrs),Material Total,Labor Total,Total\n';
    
    componentCosts.forEach(item => {
      csv += `${item.name},${item.count},$${item.materialCost.toFixed(2)},${item.laborUnits.toFixed(2)},$${item.materialTotal.toFixed(2)},$${item.laborTotal.toFixed(2)},$${item.total.toFixed(2)}\n`;
    });
    
    csv += `\nSubtotals,,,,$${subtotalMaterial.toFixed(2)},$${subtotalLabor.toFixed(2)},$${subtotal.toFixed(2)}\n`;
    csv += `Overhead (${overhead}%),,,,,,$${overheadAmount.toFixed(2)}\n`;
    csv += `Profit (${profit}%),,,,,,$${profitAmount.toFixed(2)}\n`;
    csv += `Grand Total,,,,,,$${grandTotal.toFixed(2)}\n`;
    csv += `\nTotal Labor Hours: ${totalLaborHours.toFixed(2)} hrs\n`;
    csv += `Total Components: ${componentCosts.reduce((sum, item) => sum + item.count, 0)}\n`;
    
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `cost-breakdown-${estimationId}.csv`;
    a.click();
  };

  return (
    <div className="cost-breakdown-overlay">
      <div className="cost-breakdown-container">
        {/* Header */}
        <div className="cost-breakdown-header">
          <div className="cost-breakdown-title">
            <Calculator className="cost-breakdown-icon" size={24} />
            <h2>Cost Breakdown</h2>
          </div>
          <div className="cost-breakdown-actions">
            <button onClick={exportToCSV} className="btn-secondary" title="Export to CSV">
              <Download size={18} />
              Export CSV
            </button>
            <button onClick={onClose} className="btn-close" title="Close">
              <X size={20} />
            </button>
          </div>
        </div>

        {/* Location Banner */}
        <div className="location-banner">
          <div className="location-info">
            <MapPin size={16} className={`location-icon ${isLoadingLocation ? 'loading' : ''}`} />
            <span className="location-text">
              {isLoadingLocation ? (
                'Detecting your location...'
              ) : (
                <>
                  <strong>{location}</strong> - Rates adjusted for your region
                </>
              )}
            </span>
          </div>
        </div>

        {/* Settings Panel */}
        <div className="cost-breakdown-settings">
          <div className="settings-group">
            <label>Labor Rate ($/hr)</label>
            <input
              type="number"
              value={laborRate}
              onChange={(e) => setLaborRate(parseFloat(e.target.value) || 0)}
              step="0.50"
              min="0"
            />
          </div>
          <div className="settings-group">
            <label>Material Multiplier</label>
            <input
              type="number"
              value={costMultiplier}
              onChange={(e) => setCostMultiplier(parseFloat(e.target.value) || 1.0)}
              step="0.05"
              min="0.5"
              max="2.0"
            />
          </div>
          <div className="settings-group">
            <label>Overhead (%)</label>
            <input
              type="number"
              value={overhead}
              onChange={(e) => setOverhead(parseFloat(e.target.value) || 0)}
              step="1"
              min="0"
            />
          </div>
          <div className="settings-group">
            <label>Profit Margin (%)</label>
            <input
              type="number"
              value={profit}
              onChange={(e) => setProfit(parseFloat(e.target.value) || 0)}
              step="1"
              min="0"
            />
          </div>
          <div className="settings-info">
            <p>💡 Labor units are based on NECA standards. Material costs and labor rates are automatically adjusted for your region.</p>
          </div>
        </div>

        {/* Spreadsheet */}
        <div className="cost-breakdown-content">
          {!projectKey || projectKey.components.length === 0 ? (
            <div className="cost-breakdown-empty">
              <Calculator size={48} style={{ color: 'var(--text-faint)', marginBottom: '1rem' }} />
              <h3>No Components in Key</h3>
              <p>Extract the electrical key from your blueprint to see cost breakdown.</p>
            </div>
          ) : (
          <table className="cost-breakdown-table">
            <thead>
              <tr>
                <th>Component</th>
                <th>Quantity</th>
                <th>Material Cost (ea)</th>
                <th>Labor Units (hrs)</th>
                <th>Material Total</th>
                <th>Labor Total</th>
                <th>Total</th>
              </tr>
            </thead>
            <tbody>
              {componentCosts
                .sort((a, b) => b.count - a.count) // Sort by count, highest to lowest
                .map((item, index) => {
                // Find the component color from the project key
                const component = projectKey.components.find((c: ComponentKey) => c.name === item.name);
                const componentColor = component?.color || 'var(--text-muted)';
                
                return (
                <tr key={item.name} className={item.count === 0 ? 'zero-count-row' : ''}>
                  <td className="component-name-cell">
                    <div className="component-name-with-color">
                      <div 
                        className="component-color-indicator" 
                        style={{ backgroundColor: componentColor }}
                      />
                      <span className="component-name">{item.name}</span>
                    </div>
                  </td>
                  <td className="text-center">{item.count}</td>
                  <td>
                    {item.count > 0 ? (
                      <input
                        type="number"
                        value={item.materialCost}
                        onChange={(e) => handleCostChange(index, 'materialCost', parseFloat(e.target.value) || 0)}
                        step="0.01"
                        min="0"
                        className="cost-input"
                      />
                    ) : (
                      <span className="empty-value">--</span>
                    )}
                  </td>
                  <td>
                    {item.count > 0 ? (
                      <input
                        type="number"
                        value={item.laborUnits}
                        onChange={(e) => handleCostChange(index, 'laborUnits', parseFloat(e.target.value) || 0)}
                        step="0.05"
                        min="0"
                        className="cost-input"
                      />
                    ) : (
                      <span className="empty-value">--</span>
                    )}
                  </td>
                  <td className="text-right">{item.count > 0 ? `$${item.materialTotal.toFixed(2)}` : '--'}</td>
                  <td className="text-right">{item.count > 0 ? `$${item.laborTotal.toFixed(2)}` : '--'}</td>
                  <td className="text-right total-cell">{item.count > 0 ? `$${item.total.toFixed(2)}` : '--'}</td>
                </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr className="subtotal-row">
                <td colSpan={4} className="text-right"><strong>Subtotals:</strong></td>
                <td className="text-right"><strong>${subtotalMaterial.toFixed(2)}</strong></td>
                <td className="text-right"><strong>${subtotalLabor.toFixed(2)}</strong></td>
                <td className="text-right"><strong>${subtotal.toFixed(2)}</strong></td>
              </tr>
              <tr className="overhead-row">
                <td colSpan={6} className="text-right">Overhead ({overhead}%)</td>
                <td className="text-right">${overheadAmount.toFixed(2)}</td>
              </tr>
              <tr className="profit-row">
                <td colSpan={6} className="text-right">Profit ({profit}%)</td>
                <td className="text-right">${profitAmount.toFixed(2)}</td>
              </tr>
              <tr className="grand-total-row">
                <td colSpan={6} className="text-right"><strong>Grand Total:</strong></td>
                <td className="text-right"><strong>${grandTotal.toFixed(2)}</strong></td>
              </tr>
            </tfoot>
          </table>
          )}
        </div>

        {/* Summary Footer */}
        {projectKey && projectKey.components.length > 0 && (
          <div className="cost-breakdown-footer">
            <div className="summary-stat">
              <span className="stat-label">Total Labor Hours:</span>
              <span className="stat-value">{totalLaborHours.toFixed(2)} hrs</span>
            </div>
            <div className="summary-stat">
              <span className="stat-label">Labor Rate:</span>
              <span className="stat-value">${laborRate.toFixed(2)}/hr</span>
            </div>
            <div className="summary-stat">
              <span className="stat-label">Total Components:</span>
              <span className="stat-value">{componentCosts.reduce((sum, item) => sum + item.count, 0)}</span>
            </div>
            <div className="summary-stat highlight">
              <span className="stat-label">Project Total:</span>
              <span className="stat-value">${grandTotal.toFixed(2)}</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

